/**
 * External dependencies
 */
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from '@art-of-hospitality-solutions-llc/ui-kit/components/alert';
import { Button } from '@art-of-hospitality-solutions-llc/ui-kit/components/button';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@art-of-hospitality-solutions-llc/ui-kit/components/form';
import {
  Sheet,
  SheetBody,
  SheetContent,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@art-of-hospitality-solutions-llc/ui-kit/components/sheet';
import { Checkbox } from '@art-of-hospitality-solutions-llc/ui-kit/components/checkbox';
import { zodResolver } from '@hookform/resolvers/zod';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

/**
 * Internal dependencies
 */
import Tiptap from '@Components/TipTap';
import { UpdateSpacePayload } from '@Types/api';
import { Space } from '@Types/venue';
import { normalizeRichText } from './normalizeRichText';
import service from '../../service';

const FormSchema = z.object({
  enableCorporate: z.boolean(),
  corpDetails: z.string().optional().nullable(),
  corpTerms: z.string().optional().nullable(),
  corpPolicy: z.string().optional().nullable(),
  corporateBeverageOptions: z.string().optional().nullable(),
});

type FormValues = z.infer<typeof FormSchema>;

export interface EditSpaceCorporateSheetProps extends React.PropsWithChildren {
  space: Space;
  isOpen: boolean;
  setIsOpen: (newIsOpen: boolean) => void;
}

export default function EditSpaceCorporateSheet({
  space,
  isOpen,
  setIsOpen,
}: EditSpaceCorporateSheetProps) {
  const queryClient = useQueryClient();

  const buildDefaults = (): FormValues => ({
    enableCorporate: space.bookingTypes?.includes('CORPORATE') ?? false,
    corpDetails: space.corpDetails ?? '',
    corpTerms: space.corpTerms ?? '',
    corpPolicy: space.corpPolicy ?? '',
    corporateBeverageOptions: space.corporateBeverageOptions ?? '',
  });

  const form = useForm<FormValues>({
    resolver: zodResolver(FormSchema),
    defaultValues: buildDefaults(),
  });

  useEffect(() => {
    if (isOpen) {
      form.reset(buildDefaults());
      form.clearErrors('root');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [space, isOpen]);

  const onSubmit = async (values: FormValues) => {
    // Preserve the PRIVATE_DINING booking type; this sheet only owns CORPORATE.
    const otherTypes = (space.bookingTypes ?? []).filter(
      (t) => t !== 'CORPORATE',
    );
    const bookingTypes = values.enableCorporate
      ? [...otherTypes, 'CORPORATE' as const]
      : otherTypes;

    const body: UpdateSpacePayload = {
      bookingTypes,
      corpDetails: normalizeRichText(values.corpDetails, space.corpDetails),
      corpTerms: normalizeRichText(values.corpTerms, space.corpTerms),
      corpPolicy: normalizeRichText(values.corpPolicy, space.corpPolicy),
      corporateBeverageOptions: normalizeRichText(
        values.corporateBeverageOptions,
        space.corporateBeverageOptions,
      ),
    };

    form.clearErrors('root');

    try {
      await service.updateSpace(space.id, body);

      await queryClient.invalidateQueries({ queryKey: ['space', space.id] });
      await queryClient.invalidateQueries({ queryKey: ['spaces'] });

      setIsOpen(false);
    } catch (error) {
      console.error('Error updating space:', error);
      form.setError('root', {
        type: 'server',
        message:
          'We were unable to update the corporate details. Please check your changes and try again.',
      });
    }
  };

  const handleOnOpenChange = (newIsSheetOpen: boolean) => {
    setIsOpen(newIsSheetOpen);

    if (!newIsSheetOpen) {
      form.reset(buildDefaults());
      form.clearErrors('root');
    }
  };

  const rootError = form.formState.errors.root?.message;

  return (
    <Sheet open={isOpen} onOpenChange={handleOnOpenChange}>
      <SheetContent
        onPointerDownOutside={(e) => e.preventDefault()}
        onOpenAutoFocus={(event) => event.preventDefault()}
      >
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="h-full">
            <div className="flex h-full flex-col">
              <SheetHeader>
                <SheetTitle>Edit Corporate Dining</SheetTitle>
              </SheetHeader>

              <SheetBody className="grow overflow-auto">
                {rootError && (
                  <div className="mb-4">
                    <Alert variant="destructive">
                      <AlertTitle>Something went wrong</AlertTitle>
                      <AlertDescription>{rootError}</AlertDescription>
                    </Alert>
                  </div>
                )}

                <div className="space-y-8 py-3">
                  {/* Booking Type Toggle */}
                  <FormField
                    control={form.control}
                    name="enableCorporate"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Booking Type</FormLabel>
                        <FormDescription>
                          Controls whether this space appears in the corporate
                          events section of the events app.
                        </FormDescription>
                        <div className="flex gap-2">
                          <Checkbox
                            className="mt-0.5"
                            checked={field.value}
                            onCheckedChange={(checked) =>
                              field.onChange(checked === true)
                            }
                          />
                          <div className="flex flex-col">
                            <span className="text-sm font-medium">
                              Corporate
                            </span>
                            <span className="text-muted-foreground text-xs">
                              This space will appear in the corporate events
                              section of the events app.
                            </span>
                          </div>
                        </div>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  {/* Corporate Details */}
                  <div className="space-y-4">
                    <div>
                      <h3 className="text-base font-semibold">
                        Corporate Details
                      </h3>
                      <p className="text-muted-foreground text-xs">
                        Shown to organizers booking this space for corporate
                        events.
                      </p>
                    </div>

                    <FormField
                      control={form.control}
                      name="corpDetails"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Details</FormLabel>
                          <FormControl>
                            <Tiptap
                              value={field.value || ''}
                              onChange={field.onChange}
                              onBlur={field.onBlur}
                              placeholder="Enter additional details about corporate event options, layouts, or AV capabilities..."
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="corpTerms"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Terms</FormLabel>
                          <FormControl>
                            <Tiptap
                              value={field.value || ''}
                              onChange={field.onChange}
                              onBlur={field.onBlur}
                              placeholder="Enter terms specific to corporate event bookings..."
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="corpPolicy"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Policy</FormLabel>
                          <FormControl>
                            <Tiptap
                              value={field.value || ''}
                              onChange={field.onChange}
                              onBlur={field.onBlur}
                              placeholder="Enter cancellation and house policy for corporate events..."
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="corporateBeverageOptions"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Beverage Options</FormLabel>
                          <FormControl>
                            <Tiptap
                              value={field.value || ''}
                              onChange={field.onChange}
                              onBlur={field.onBlur}
                              placeholder="Enter beverage packages, bar options, wine list details..."
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                </div>
              </SheetBody>

              <SheetFooter>
                <Button
                  type="submit"
                  variant="primary"
                  loading={form.formState.isSubmitting}
                >
                  Update
                </Button>
                <Button
                  variant="secondary"
                  onClick={() => handleOnOpenChange(false)}
                >
                  Cancel
                </Button>
              </SheetFooter>
            </div>
          </form>
        </Form>
      </SheetContent>
    </Sheet>
  );
}
